import { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { Box, CircularProgress, capitalize } from "@mui/material";
import { UBButton, HeadingText } from "../../../../Libraries";
import { useLocation, useNavigate } from "react-router-dom";
import BusinessOverviewCard from "./BusinessOverviewCard";
import BusinessOverviewCardSkeleton from "./BusinessOverviewCardSkeleton";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { useAuthContext } from "../../../../context/AuthContext";
import NonIdealNew from "../../../common/NonIdealNew";
import RecentTransactionList from "./RecentTransactionList";
import styles from "./styles.module.css";
import { useBusinessOverview } from "../../../../hooks/useBusinessOverview";
import moment from "moment";
import { formatRange, getStatementDateConfig } from "../../../../utils";
import {
  BusinessOverviewTransactionsCount,
  observeAllExpensesByBusinessCount,
  observeAllSaleByBusinessCount,
} from "../../../../database/helpers/invoice";
import withObservables from "@nozbe/with-observables";

const { minDate, maxDate } = getStatementDateConfig();
const defaultStart = moment().subtract(29, "days").startOf("day").toDate();
const defaultEnd = moment().endOf("day").toDate();

const getStoredFilter = (key) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key));
    if (!saved?.start_date || !saved?.end_date) return null;
    return { start_date: new Date(saved.start_date), end_date: new Date(saved.end_date) };
  } catch { return null; }
};

const BusinessOverviewList = ({
  saleCount,
  expenseCount,
  businessOverviewTransactionsListCount,
}) => {
  const [I18n] = useContext(LanguageContext);
  const { business, user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [transactionIsLoading, setTransactionIsLoading] = useState(true);

  const {
    handleGetProfitAndLossOverview,
    handleGetCashFLowOverview,
    handleGetStockOverview,
    profit,
    total_expenses,
    total_sales,
    money_in,
    money_out,
    net_cash_flow,
    total_cost_value,
    total_items_in_stock,
    low_stock_items,
    pnl_loading,
    cf_loading,
    stock_loading,
  } = useBusinessOverview({ business_id: business?.id, navigate });

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const pnl = getStoredFilter("bo_pnl_filter");
    const cf = getStoredFilter("bo_cf_filter");
    return {
      pnl_filter: { start_date: pnl?.start_date || defaultStart, end_date: pnl?.end_date || defaultEnd },
      cf_filter: { start_date: cf?.start_date || defaultStart, end_date: cf?.end_date || defaultEnd },
      stock_filter: { start_date: defaultStart, end_date: defaultEnd },
    };
  });

  useEffect(() => {
    const pnl = getStoredFilter("bo_pnl_filter");
    const cf = getStoredFilter("bo_cf_filter");
    handleGetProfitAndLossOverview({ startDate: pnl?.start_date || defaultStart, endDate: pnl?.end_date || defaultEnd });
    handleGetCashFLowOverview({ startDate: cf?.start_date || defaultStart, endDate: cf?.end_date || defaultEnd });
    handleGetStockOverview({ startDate: defaultStart, endDate: defaultEnd });
  }, []);

  const filterConfig = {
    pnl: {
      key: "pnl_filter",
      api: handleGetProfitAndLossOverview,
    },
    cf: {
      key: "cf_filter",
      api: handleGetCashFLowOverview,
    },
    stock: {
      key: "stock_filter",
      api: handleGetStockOverview,
    },
  };

  const handleApplyOverviewFilter = (startDate, endDate, type) => {
    const config = filterConfig[type];

    if (!config) return;

    const isValid = startDate && endDate;

    setSelectedFilters((prev) => ({
      ...prev,
      [config.key]: {
        ...prev[config.key],
        start_date: isValid ? startDate : "",
        end_date: isValid ? endDate : "",
      },
    }));

    if (isValid) {
      if (type === "pnl") sessionStorage.setItem("bo_pnl_filter", JSON.stringify({ start_date: startDate, end_date: endDate }));
      else if (type === "cf") sessionStorage.setItem("bo_cf_filter", JSON.stringify({ start_date: startDate, end_date: endDate }));
    }

    config.api(isValid ? { startDate, endDate } : {});
  };

  const handleAddQuickSaleOrExpense = (type) => {
    triggerLogEvent(analytics, type === "in" ? "bo_nav_add_sale" : "bo_nav_add_expense");
    navigate(
      type === "in" ? "/QuickSale/QuickSaleForm" : "/Expense/ExpenseForm",
      {
        state: {
          pathname: location?.pathname,
        },
      },
    );
  };

  const handleViewAllTransactions = () => {
    triggerLogEvent(analytics, "business_overview_view_all_transactions");
    navigate("/BusinessOverviewTransactions");
  };

  const butttonList = [
    {
      title: I18n.add_sale,
      onClick: () => handleAddQuickSaleOrExpense("in"),
    },
    {
      title: I18n.add_expense,
      onClick: () => handleAddQuickSaleOrExpense("out"),
    },
    {
      title: `${I18n.start} ${I18n.pos}`,
      onClick: () => {
        triggerLogEvent(analytics, "bo_nav_start_pos");
        navigate("/SaleBook", {
          state: {
            pathname: location?.pathname,
          },
        });
      },
    },
    {
      title: `${I18n.add_stock}`,
      onClick: () => {
        triggerLogEvent(analytics, "bo_nav_add_stock");
        navigate("/Inventory/Create");
      },
    },
    {
      title: I18n.viewPAndL,
      onClick: () => {
        triggerLogEvent(analytics, "bo_view_pnl");
        navigate("/ProfitAndLoss");
      },
    },
  ];

  const isIdealTransactions = businessOverviewTransactionsListCount > 0;

  return (
    <Box className={`${styles.businessOverviewContent} generic-scrollbar`}>
      <Box className={styles.actionButtonsRow} display="flex">
        {butttonList.map((btn, index) => (
          <UBButton
            key={index}
            onClick={btn.onClick}
            id="addContactButton"
            className={`${index === 0 ? "ubPrimaryButton" : "ubSecondaryButton"} fontSize12 fontWeight400 textUppercase ${styles.mainButton}`}
            title={btn.title}
          />
        ))}
      </Box>

      <Box className={styles.cardContainer}>
        {pnl_loading ? (
          <BusinessOverviewCardSkeleton />
        ) : (
          <BusinessOverviewCard
            title={"profit_Loss"}
            dateConfig={{
              onExport: (startDate, endDate) => {
                handleApplyOverviewFilter(startDate, endDate, "pnl");
              },
              applyBtnText: formatRange(selectedFilters.pnl_filter, I18n),
              spinnerColor: "newFontColor",
              selectedDate: selectedFilters.pnl_filter,
              minDate,
              maxDate,
            }}
            total={formatNum(profit || 0)}
            totalSubTitle={profit > 0 ? "profit" : "loss"}
            firstCountLabel={`${I18n.gross_profit}`}
            firstCountValue={
              saleCount
                ? `+ Rs.${formatNum(total_sales || 0)}`
                : capitalize(I18n.add_sale)
            }
            firstCountValueClassName={
              saleCount == 0 ? styles.firstCountValueNonIdeal : ""
            }
            onFirstCountValueClick={() => handleAddQuickSaleOrExpense("in")}
            secondCountLabel={`${I18n.expenses}`}
            secondCountValue={
              expenseCount
                ? `- Rs.${formatNum(total_expenses || 0)}`
                : capitalize(I18n.add_expense)
            }
            secondCountValueClassName={
              expenseCount == 0 ? styles.firstCountValueNonIdeal : ""
            }
            onSecondCountValueClick={() => handleAddQuickSaleOrExpense("out")}
            handleSeeDetail={() => {
              triggerLogEvent(analytics, "bo_view_pnl");
              navigate("/ProfitAndLoss");
            }}
          />
        )}
        {cf_loading ? (
          <BusinessOverviewCardSkeleton />
        ) : (
          <BusinessOverviewCard
            title={"cash_flow"}
            dateConfig={{
              onExport: (startDate, endDate) => {
                handleApplyOverviewFilter(startDate, endDate, "cf");
              },
              applyBtnText: formatRange(selectedFilters.cf_filter, I18n),
              spinnerColor: "newFontColor",
              selectedDate: selectedFilters.cf_filter,
              minDate,
              maxDate,
            }}
            total={formatNum(net_cash_flow || 0)}
            totalSubTitle={"net_cash"}
            firstCountLabel={`${I18n.gross_cash_inflow}`}
            firstCountValue={`+ Rs.${formatNum(money_in || 0)}`}
            secondCountLabel={`${I18n.gross_cash_outflow}`}
            secondCountValue={`- Rs.${formatNum(money_out || 0)}`}
            handleSeeDetail={() => {
              triggerLogEvent(analytics, "bo_view_cashflow");
              navigate("/CashFlow");
            }}
          />
        )}
        {stock_loading ? (
          <BusinessOverviewCardSkeleton />
        ) : (
          <BusinessOverviewCard
            title={"inventory_value"}
            dateConfig={{
              onExport: (startDate, endDate) => {
                handleApplyOverviewFilter(startDate, endDate, "stock");
              },
              applyBtnText: formatRange(selectedFilters.stock_filter, I18n),
              spinnerColor: "newFontColor",
              selectedDate: selectedFilters.stock_filter,
            }}
            total={formatNum(total_cost_value || 0)}
            totalSubTitle={"total_cost_value"}
            firstCountLabel={"total_item_in_stock"}
            firstCountValue={`${total_items_in_stock || 0} ${I18n.items}`}
            secondCountLabel={"low_stock_items"}
            secondCountValue={`${low_stock_items || 0} ${I18n.items}`}
            handleSeeDetail={() => {
              triggerLogEvent(analytics, "bo_view_stock");
              navigate("/Inventory");
            }}
          />
        )}
      </Box>

      <Box className={styles.tranactionListingWrapper}>
        <Box className={styles.tranactionListingTop}>
          <HeadingText tag="h3" className={styles.transactionsTitle}>
            {I18n.transactions}
          </HeadingText>
          {isIdealTransactions ? (
            <button
              id="business_overview_transactions_see_more_btn"
              type="button"
              className={styles.seeMoreLink}
              onClick={handleViewAllTransactions}
            >
              {I18n.see_more}
            </button>
          ) : null}
        </Box>

        {isIdealTransactions ? (
          <>
            {transactionIsLoading ? (
              <Box className={styles.transactionListLoadingBox}>
                <CircularProgress size={25} />
              </Box>
            ) : null}
            <Box
              className={`${styles.transactionsTable} ${
                transactionIsLoading ? styles.transactionsTableHidden : ""
              }`}
            >
              <RecentTransactionList
                hideTitle
                onLoadingChange={setTransactionIsLoading}
                accountWrapperClassName={styles.transactionsTableInner}
                accountListBodyWarpperClassName="transactionItemListWrapper"
                business={business}
                user={user}
              />
            </Box>
          </>
        ) : (
          <Box className={styles.transactionListNonIdealWrapper}>
            <NonIdealNew
              title={`${I18n.sales}, ${I18n.expenses}, ${I18n.profit}`}
              subtitle={I18n.Add_salesexpenses_to_see_your_PL_and_grow_your_profits}
              button1Title="add_expense"
              onButton1Click={() => handleAddQuickSaleOrExpense("out")}
              loading={false}
              disabledOne={false}
              buttonOneId="create_expense"
              listButton2Title="add_sale"
              onListButton2Click={() => handleAddQuickSaleOrExpense("in")}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const enhance = withObservables([], () => ({
  saleCount: observeAllSaleByBusinessCount(localStorage.getItem("business_id")),
  expenseCount: observeAllExpensesByBusinessCount(
    localStorage.getItem("business_id"),
  ),
  businessOverviewTransactionsListCount: BusinessOverviewTransactionsCount(
    localStorage.getItem("business_id"),
  ),
}));

export default enhance(BusinessOverviewList);
